import { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useUnistyles } from 'react-native-unistyles';
import { View } from 'react-native';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import IconButton from '@/components/General/IconButton';
import DropdownMenu from '@/components/General/DropdownMenu';
import {
    STEP_TYPE_CONFIG, ACTION_TYPE_LABELS, ADVANCE_LABELS, FONT,
    NODE_WIDTH, STEP_HEIGHT, TASK_STEP_HEIGHT,
} from '../constants';
import { buildSourceHandleId, TARGET_HANDLE_ID } from '../handles';
import { resolveActionValue, resolveTaskStepInfo } from '../actionValues';
import { isApprovalStep, stepBranches } from '@/utils/automationBuilder';

function BranchHandle({ id, index, total, readOnly }) {
    const left = total === 1 ? '50%' : `${((index + 0.5) / total) * 100}%`;
    return (
        <Handle
            type="source"
            position={Position.Bottom}
            id={id}
            isConnectableStart={!readOnly}
            isConnectableEnd={false}
            style={{
                background: '#f59e0b',
                width: 10,
                height: 10,
                border: '2px solid #fff',
                bottom: -5,
                left,
                transform: 'translateX(-50%)',
            }}
        />
    );
}

export default function StepNode({ data, selected }) {
    const { theme } = useUnistyles();
    const { step, highlightVariant, onEdit, onMenu, readOnly } = data;
    const config = STEP_TYPE_CONFIG[step.stepType] || STEP_TYPE_CONFIG.ACTION;
    const dim = data.dim;
    const isInvalid = data.isInvalid;
    const isDark = theme.scheme === 'dark';
    const [menuOpen, setMenuOpen] = useState(false);

    const isCondition = step.stepType === 'CONDITION';
    // REQUEST_APPROVAL actions branch on a fixed Approved/Denied list.
    const isApproval = isApprovalStep(step);
    const isBranchSource = isCondition || isApproval;
    const branches = stepBranches(step);
    const actionLabel = step.actionType ? (ACTION_TYPE_LABELS[step.actionType] || step.actionType) : null;
    const advanceLabel = step.advanceOn ? (ADVANCE_LABELS[step.advanceOn] || step.advanceOn) : null;
    const actionValue = step.stepType === 'ACTION' ? resolveActionValue(step, data.actionLookups) : null;
    const taskInfo = step.stepType === 'TASK'
        ? resolveTaskStepInfo(step, data.taskLookups, data.enrollmentStepDetails, data.enrollee)
        : null;
    const displayName = step.name?.trim() || actionLabel || config.label;

    // DELAY static-date-in-past warning
    const isDelayStaticPast = step.stepType === 'DELAY'
        && step.config?.delayType === 'STATIC_DATE'
        && step.config?.staticDate
        && new Date(step.config.staticDate) < new Date();
    const delayDetail = step.stepType === 'DELAY'
        ? (step.config?.delayType === 'STATIC_DATE' && step.config?.staticDate
            ? new Date(step.config.staticDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
            : (step.config?.duration ? `Wait ${step.config.duration} ${step.config.unit || 'days'}` : null))
        : null;

    const highlightColor = highlightVariant ? theme.colors[highlightVariant] : null;
    const highlightBg = highlightColor ? highlightColor + '10' : null;

    const menuOptions = [
        { name: 'Edit', icon: 'create-outline', onSelect: () => onMenu?.(step, 'edit') },
        { name: 'Duplicate', icon: 'copy-outline', onSelect: () => onMenu?.(step, 'duplicate') },
        { name: 'Move Up', icon: 'arrow-up-outline', onSelect: () => onMenu?.(step, 'moveUp') },
        { name: 'Move Down', icon: 'arrow-down-outline', onSelect: () => onMenu?.(step, 'moveDown') },
        { name: 'Insert step after', icon: 'add-outline', onSelect: () => onMenu?.(step, 'insertAfter') },
        { name: 'Detach step', icon: 'unlink-outline', onSelect: () => onMenu?.(step, 'detachStep') },
    ];

    return (
        <div
            style={{
                minWidth: NODE_WIDTH,
                maxWidth: NODE_WIDTH,
                height: step.stepType === 'TASK' ? TASK_STEP_HEIGHT : STEP_HEIGHT,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                background: highlightBg || (isDark ? theme.colors.surface : '#fff'),
                border: `1.5px solid ${isInvalid ? theme.colors.error : (highlightColor || theme.colors.border)}`,
                borderRadius: 14,
                overflow: 'visible',
                boxShadow: isInvalid
                    ? `0 0 0 2px ${theme.colors.error}20, 0 4px 20px rgba(0,0,0,0.12)`
                    : (highlightColor
                        ? `0 0 0 2px ${highlightColor}20, 0 4px 20px rgba(0,0,0,0.12)`
                        : '0 2px 12px rgba(0,0,0,0.07)'),
                fontFamily: FONT,
                position: 'relative',
                filter: dim ? 'grayscale(0.6) opacity(0.45)' : undefined,
                outline: selected ? `2px solid ${theme.colors.primary}` : 'none',
                outlineOffset: '-2px',
            }}
            onDoubleClick={() => onEdit?.(step)}
        >
            <Handle
                type="target"
                position={Position.Top}
                id={TARGET_HANDLE_ID}
                isConnectableEnd={!readOnly}
                isConnectableStart={false}
                style={{
                    background: theme.colors.primary,
                    width: 10,
                    height: 10,
                    border: `2px solid ${isDark ? theme.colors.surface : '#fff'}`,
                    top: -5,
                }}
            />

            {isBranchSource ? (
                isCondition && branches.length === 0 ? (
                    <Handle
                        type="source"
                        position={Position.Bottom}
                        id={buildSourceHandleId('default')}
                        isConnectableStart={!readOnly}
                        isConnectableEnd={false}
                        style={{
                            background: '#94a3b8',
                            width: 10,
                            height: 10,
                            border: '2px solid #fff',
                            bottom: -5,
                            left: '50%',
                            transform: 'translateX(-50%)',
                        }}
                    />
                ) : (
                    <>
                        {branches.map((b, i) => (
                            <BranchHandle
                                key={b.id}
                                id={buildSourceHandleId(b.id)}
                                index={i}
                                total={isApproval ? branches.length : branches.length + 1}
                                readOnly={readOnly}
                            />
                        ))}
                        {!isApproval && (
                            <BranchHandle
                                id={buildSourceHandleId('default')}
                                index={branches.length}
                                total={branches.length + 1}
                                readOnly={readOnly}
                            />
                        )}
                    </>
                )
            ) : (
                <Handle
                    type="source"
                    position={Position.Bottom}
                    id="out"
                    isConnectableStart={!readOnly}
                    isConnectableEnd={false}
                    style={{
                        background: theme.colors.primary,
                        width: 10,
                        height: 10,
                        border: `2px solid ${isDark ? theme.colors.surface : '#fff'}`,
                        bottom: -5,
                    }}
                />
            )}

            {/* Header */}
            <div style={{
                padding: '10px 14px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderTop: `3px solid ${theme.colors[config.colorVariant] || theme.colors.primary}`,
                borderRadius: '10px 10px 0 0',
                borderBottom: `1px solid ${theme.colors.border}`,
            }}>
                <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: (theme.colors[config.colorVariant + 'Container'] || theme.colors.surfaceVariant),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    flexShrink: 0,
                }}>
                    <Unicon
                        name={config.icon}
                        size={16}
                        color={theme.colors[config.colorVariant] || theme.colors.primary}
                    />
                </div>
                <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: theme.colors[config.colorVariant] || theme.colors.primary,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    flex: 1,
                }}>
                    {config.label}
                </span>
                {isDelayStaticPast && (
                    <span
                        title="This path won't ever run past this point — the static date is in the past."
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: theme.colors.warning,
                            fontSize: 14,
                            cursor: 'help',
                        }}
                    >
                        <Unicon name="warning-outline" size={16} colorVariant="warning" />
                    </span>
                )}
                {!readOnly && (
                    <DropdownMenu
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        options={menuOptions}
                        triggerComponent={
                            <View testID={`path-step-menu-${step.id}`}>
                                <IconButton
                                    iconName="ellipsis-vertical-outline"
                                    iconSize={18}
                                    onPress={() => setMenuOpen(true)}
                                />
                            </View>
                        }
                    />
                )}
            </div>

            {/* Body */}
            <div style={{ padding: '10px 14px', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
                    {displayName}
                </div>
                {step.description ? (
                    <div style={{
                        fontSize: 12, color: theme.colors.textSecondary, marginBottom: 4,
                        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
                        overflow: 'hidden',
                    }}>
                        {step.description}
                    </div>
                ) : null}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                    {actionLabel && (
                        <span style={{ fontSize: 10, color: theme.colors.textSecondary, background: theme.colors.surfaceVariant, padding: '2px 8px', borderRadius: 10 }}>
                            {actionLabel}
                        </span>
                    )}
                    {advanceLabel && (
                        <span style={{ fontSize: 10, color: theme.colors.textSecondary, background: theme.colors.surfaceVariant, padding: '2px 8px', borderRadius: 10 }}>
                            {advanceLabel}
                        </span>
                    )}
                    {step.isEnabled === false && (
                        <span style={{ fontSize: 10, color: theme.colors.error, background: theme.colors.errorContainer, padding: '2px 8px', borderRadius: 10 }}>
                            Disabled
                        </span>
                    )}
                    {highlightVariant === 'error' && (
                        <span style={{ fontSize: 10, color: theme.colors.error, background: theme.colors.errorContainer, padding: '2px 8px', borderRadius: 10 }}>
                            Failed
                        </span>
                    )}
                </div>
                {actionValue && (
                    <div style={{
                        fontSize: 12, color: theme.colors.text, fontWeight: 500, marginTop: 6,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {actionValue}
                    </div>
                )}
                {delayDetail && (
                    <div style={{
                        fontSize: 12, color: isDelayStaticPast ? theme.colors.warning : theme.colors.text, fontWeight: 500, marginTop: 6,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {delayDetail}
                    </div>
                )}
                {taskInfo && (
                    <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 12, color: theme.colors.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {taskInfo.taskName}
                        </div>
                        <div style={{ fontSize: 11, color: theme.colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {taskInfo.strategyLabel}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, marginTop: 2 }}>
                            {taskInfo.actualAssignee && taskInfo.assigneeName && (
                                <div style={{ flexShrink: 0 }}>
                                    <Avatar size={18} profile={taskInfo.actualAssignee} disableBorder />
                                </div>
                            )}
                            <div style={{ minWidth: 0, flex: 1, fontSize: 11, color: taskInfo.isWaitingOnTask ? theme.colors.warning : theme.colors.textSecondary, fontWeight: taskInfo.isWaitingOnTask ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {taskInfo.isWaitingOnTask
                                    ? `Waiting on task — ${taskInfo.assigneeName ? `assigned to ${taskInfo.assigneeName}` : 'unassigned'}`
                                    : taskInfo.assigneeLabel}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
